// Fachsprache und Aufrufe der Stichworte (ST-TAX-002, erster Schnitt;
// ADR-0053).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// interactions.ts und identifiers.ts und aus demselben Grund.
//
// DIE WOERTER "TAXONOMY", "TERM", "CLASSIFICATION", "ASSIGNMENT" UND
// "OPERATING CONTEXT" KOMMEN AUF DEM BILDSCHIRM NICHT VOR (NFR-UX-003,
// CM-UX-010; Story-UX 5.2.13). Auf dem Bildschirm heisst es "Stichwort",
// "Definition", "vergeben", "entfernen", "Stichwortliste". Eine Probe
// (classifications.test.ts) haelt jeden Text dieser Datei gegen eine
// Verbotsliste.
//
// KEIN ENTWURF IM BROWSER. Die Eingabe ist eine AUSWAHL und kein Text; es gibt
// nichts zu verlieren und nichts zwischenzuspeichern (Story-UX, NFR-UX-005).
// Damit entfaellt die ganze localStorage-Familie aus interactions.ts samt
// ihrem Risiko (Threat Model der Notiz, TM-008).

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Die Herkunft einer Zuordnung in Worten (Story-UX 5.2.5, Source-aware). */
export const SOURCE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  manual: 'von Hand vergeben',
  migration: 'aus dem Altbestand übernommen',
  rule: 'durch eine Regel vorgeschlagen',
  enrichment: 'aus einer Anreicherung übernommen',
  ai_suggestion: 'von der KI vorgeschlagen',
});

/** Rueckfall: der Rohwert sichtbar unuebersetzt, statt ein erfundenes Wort. */
export function sourceLabel(code: string): string {
  return SOURCE_LABELS[code] ?? code;
}

/**
 * Der Lebenszyklus eines Begriffs in Worten -- gebraucht wird er nur fuer die
 * Ausnahme: eine BESTEHENDE Zuordnung auf einem Begriff, der inzwischen nicht
 * mehr vergeben werden soll, bleibt sichtbar und wird erklaert (Frage 7,
 * 15.09.2026). Ein aktiver Begriff bekommt keinen Hinweis -- ein Etikett, das
 * an jeder Zeile steht, sagt nichts.
 */
export function termStatusHint(termStatus: string): string | undefined {
  if (termStatus === 'deprecated') {
    return 'Dieses Stichwort wird nicht mehr vergeben. Die bestehende Zuordnung bleibt erhalten.';
  }
  if (termStatus === 'blocked') {
    return 'Dieses Stichwort ist gesperrt. Die bestehende Zuordnung bleibt erhalten und sollte geprüft werden.';
  }
  if (termStatus === 'draft') {
    return 'Dieses Stichwort ist noch ein Entwurf und kann nicht neu vergeben werden.';
  }
  return undefined;
}

export interface ClassificationView {
  readonly classificationAssignmentId: string;
  readonly taxonomyTermId: string;
  readonly displayName: string;
  readonly definition: string | null;
  readonly taxonomyId: string;
  readonly taxonomyDisplayName: string;
  readonly isMultiValued: boolean;
  readonly termStatus: string;
  readonly sourceType: string;
  readonly assignedAt: string;
  readonly validFrom: string;
  /** Wurde das Stichwort durch ein anderes ersetzt? */
  readonly replaced: boolean;
  /** Das vergebbare Ende der Ersetzungskette, wenn es eines gibt und du es sehen darfst. */
  readonly replacedByDisplayName: string | null;
}

/**
 * Der Hinweis zu einem ersetzten Stichwort. Mit sichtbarem, vergebbarem
 * Nachfolger nennt er ihn; sonst sagt er nur "abgelöst" -- einen beendeten
 * oder fremden Nachfolger zu empfehlen wäre ein falscher nächster Schritt.
 */
export function replacedHint(classification: Pick<ClassificationView, 'replaced' | 'replacedByDisplayName'>): string | undefined {
  if (!classification.replaced) return undefined;
  return classification.replacedByDisplayName === null
    ? 'Abgelöst. Dieses Stichwort wird nicht mehr vergeben. Du kannst es entfernen, wenn es nicht mehr passt.'
    : `Abgelöst durch „${classification.replacedByDisplayName}“. Dieses Stichwort wird nicht mehr vergeben. Vergib „${classification.replacedByDisplayName}“, wenn es noch passt, und entferne dann dieses Stichwort.`;
}

/** Der eine Hinweis zu einer bestehenden Zuordnung: Ersetzung vor Lebenszyklus. */
export function assignmentHint(classification: ClassificationView): string | undefined {
  return replacedHint(classification) ?? termStatusHint(classification.termStatus);
}

export interface EndedClassificationView {
  readonly classificationAssignmentId: string;
  readonly taxonomyTermId: string;
  readonly displayName: string;
  readonly definition: string | null;
  readonly taxonomyDisplayName: string;
  readonly sourceType: string;
  readonly assignedAt: string;
  readonly validFrom: string;
  readonly endedAt: string;
}

export interface AssignableTermView {
  readonly taxonomyTermId: string;
  readonly displayName: string;
  readonly definition: string;
  readonly stableCode: string;
  readonly taxonomyId: string;
  readonly taxonomyDisplayName: string;
  readonly isMultiValued: boolean;
}

export type ClassificationsResult =
  | {
      readonly outcome: 'loaded';
      readonly classifications: readonly ClassificationView[];
      readonly endedClassifications: readonly EndedClassificationView[];
      readonly additionalClassificationCount: number;
      readonly endedClassificationsTruncated: boolean;
    }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type TermsResult =
  | { readonly outcome: 'loaded'; readonly terms: readonly AssignableTermView[]; readonly truncated: boolean }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type AssignResult =
  | { readonly outcome: 'assigned' }
  | { readonly outcome: 'not-found'; readonly what: 'party' | 'term' }
  | { readonly outcome: 'invalid'; readonly errors: readonly { readonly fieldPath: string; readonly reasonCode: string }[] }
  | {
      readonly outcome: 'rejected';
      readonly message: PlainMessage;
      readonly kind: 'conflict' | 'other';
      readonly reasonCode?: string | undefined;
    };

export type EndResult =
  | { readonly outcome: 'ended' }
  | { readonly outcome: 'not-found' }
  | {
      readonly outcome: 'rejected';
      readonly message: PlainMessage;
      readonly kind: 'conflict' | 'other';
      readonly reasonCode?: string | undefined;
    };

// --- Die Saetze: Problem, Wirkung, naechster Schritt (NFR-UX-005) -----------

/**
 * Der Leerzustand ist WAHR und keine Vertroestung. Zwischen dem Ausrollen
 * dieses Schnitts und dem der Vokabularmigration gibt es tatsaechlich keine
 * Stichworte -- die Oberflaeche sagt das und schaltet die Hauptaktion ab,
 * statt eine Auswahl anzubieten, die leer aufginge (ADR-0002, ADR-0053
 * Ziffer 11; Story-UX 5.2.14 Calm technology).
 */
export const NO_VOCABULARY_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Noch keine Stichworte eingerichtet.',
  explanation:
    'Stichworte werden zentral festgelegt, damit die ganze Gruppe dieselben Begriffe verwendet. Für diese Installation ist noch keine Liste eingerichtet.',
  nextStep: 'Sobald die Liste eingerichtet ist, kannst du hier Stichworte vergeben. Bis dahin ist nichts zu tun.',
  retryable: false,
});

export const DUPLICATE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort ist bereits vergeben.',
  explanation: 'Der Kontakt trägt es schon; ein zweites Mal wird es nicht vergeben.',
  nextStep: 'Die Liste oben zeigt den gespeicherten Stand.',
  retryable: false,
  // Der gewollte Zustand gilt schon: ein Hinweis, kein Fehler.
  notice: true,
});

/**
 * Das Vergeben blieb ohne Antwort (Netzabbruch). Ob es angekommen ist, weiss
 * niemand; die still nachgeladene Liste zeigt den gespeicherten Stand. Ein
 * zweites Vergeben ist sicher: es endet hoechstens in "bereits vergeben".
 */
export const ASSIGN_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob das Stichwort vergeben wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an. Die Liste ist neu geladen und zeigt den gespeicherten Stand.',
  nextStep: 'Fehlt das Stichwort dort, vergib es erneut.',
  retryable: true,
});

/** Das Entfernen blieb ohne Antwort -- dieselbe Lage wie beim Vergeben. */
export const END_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob das Stichwort entfernt wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an. Die Liste ist neu geladen und zeigt den gespeicherten Stand.',
  nextStep: 'Steht das Stichwort noch dort, entferne es erneut.',
  retryable: true,
});

export const ALREADY_ENDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort wurde bereits entfernt.',
  explanation: 'Jemand anderes hat es entfernt, während diese Seite offen war.',
  // Der Abschnitt laedt nach jedem Konflikt beim Entfernen nach.
  nextStep: 'Die Liste ist neu geladen.',
  retryable: false,
  notice: true,
});

export const PARTY_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt können keine Stichworte mehr geändert werden.',
  explanation: 'Der Kontakt ist zusammengeführt, archiviert oder anonymisiert. Sein Stand ist abgeschlossen.',
  nextStep: 'Öffne den aktuellen Kontakt über die Kontaktliste.',
  retryable: false,
});

/**
 * Der Server sendet EINEN Grundcode fuer vier Ursachen: der Begriff ist nicht
 * mehr aktiv, er steht ausserhalb seines Geltungszeitraums, seine Achse ist
 * stillgelegt, oder sie ist nicht gruppenweit. Welche davon zutrifft, sagt er
 * nicht -- und das ist richtig so: der Unterschied aendert fuer die Nutzerin
 * nichts am naechsten Schritt.
 *
 * DESHALB DARF DER SATZ KEINE EINZELNE URSACHE BEHAUPTEN. Die erste Fassung
 * sagte "Es wurde zurueckgezogen" -- das waere bei einer stillgelegten Achse
 * oder einem abgelaufenen Geltungszeitraum schlicht falsch gewesen, und eine
 * falsche Begruendung ist schlechter als keine (Disposition des Dirigenten zu
 * Punkt 2, 17.09.2026: den TEXT unterscheiden, nicht den Code -- hier heisst
 * das: den Text die ganze Klasse benennen lassen).
 */
export const TERM_NOT_ASSIGNABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort kann nicht mehr vergeben werden.',
  explanation:
    'Es wurde zurückgezogen, seine Gültigkeit ist abgelaufen, oder die Stichwortliste, zu der es gehört, wird nicht mehr geführt.',
  nextStep: 'Lade den Kontakt neu; die Auswahl zeigt dann die Stichworte, die du vergeben kannst.',
  retryable: true,
});

export const TERM_UNKNOWN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort gibt es hier nicht.',
  explanation: 'Es wurde entfernt oder gehört zu einem Verlag, für den du nicht berechtigt bist.',
  nextStep: 'Lade den Kontakt neu; die Auswahl zeigt dann die verfügbaren Stichworte.',
  retryable: true,
});

/**
 * Der Satz, wenn der Server mehr Stichworte hat, als er geschickt hat.
 *
 * KEINE SPERRE, sondern eine Auskunft: die Auswahl bleibt bedienbar, und wer
 * eines der gezeigten Stichworte vergeben will, kann das. Eine Sperre waere
 * eine Funktion, die niemand angefordert hat (ADR-0025 Punkt 1) -- und sie
 * naehme der Nutzerin etwas weg, statt ihr etwas zu sagen. Die Suche ueber
 * Name und Alias ist der benannte Reviewpunkt (ADR-0053 Ziffer 9); `truncated`
 * ist genau ihr Ausloeser.
 *
 * Ohne diesen Satz waere ab dem 51. Stichwort jedes weitere unvergebbar, und
 * niemand erfuehre es (Codex-Cloud-Review #254, 17.09.2026, P2).
 */
export function truncationNotice(shown: number): string {
  return `Es werden ${shown} Stichworte zur Auswahl angezeigt. Es gibt mehr, als hier hineinpassen — fehlt dir eines, wende dich an die LIAISON-Administration.`;
}

/**
 * Der Satz VOR dem Entfernen (NFR-UX-005, CM-UX-004): er nennt die Wirkung
 * und den Rueckweg. Ein Stichwort zu entfernen ist umkehrbar -- es wird
 * beendet, nicht geloescht, und kann neu vergeben werden. Genau das sagt der
 * Satz, damit die Bestaetigung eine Entscheidung ist und keine Hürde.
 */
export function removalConfirmation(displayName: string): string {
  return `„${displayName}" wird von diesem Kontakt entfernt. Der bisherige Stand bleibt im Verlauf sichtbar, und du kannst das Stichwort jederzeit erneut vergeben.`;
}

/** Die Bestaetigung NACH dem Vergeben -- fuer die Statusregion des Abschnitts. */
export function assignedAnnouncement(displayName: string): string {
  return `Stichwort „${displayName}" vergeben.`;
}

export function endedAnnouncement(displayName: string): string {
  return `Stichwort „${displayName}" entfernt.`;
}

/**
 * Der Hinweis auf eine Mehrfachvergabe an einer Achse, die nur einen Wert
 * vorsieht. KEINE Abweisung (ADR-0030 Punkt 4, Pflichtenheft 6.2): der
 * Konflikt wird gezeigt, in Worten und nicht allein in Farbe (NFR-ACC-001).
 */
export function cardinalityHint(taxonomyDisplayName: string, count: number): string | undefined {
  if (count < 2) {
    return undefined;
  }
  return `Für „${taxonomyDisplayName}" ist eigentlich ein Stichwort je Kontakt vorgesehen; hier sind es ${count}. Das ist erlaubt und steht unter ${KONFLIKTE_SEITE}.`;
}

/** Das letzte Wort des Hinweises; die Kontaktseite setzt es als Link auf die Seite gleichen Namens (Glossar). */
export const KONFLIKTE_SEITE = 'Stichwortkonflikte';
export const KONFLIKTE_PFAD = '/datenqualitaet';

/**
 * Die Hinweise eines Abschnitts, aus den GELIEFERTEN Zeilen abgeleitet.
 *
 * Die Oberflaeche raet nicht und schlaegt nichts nach: jede Zeile bringt ihre
 * Achse und deren Kardinalitaet mit (ADR-0053 Ziffer 6). Das ist der Grund,
 * warum der Leseweg beides ausliefert -- in der Begriffsliste steht ein
 * zurueckgezogener Begriff naemlich gar nicht mehr, und ein Nachschlagen dort
 * fuende die Achse einer Altzuordnung nicht.
 */
export function cardinalityHints(classifications: readonly ClassificationView[]): readonly string[] {
  const zaehler = new Map<string, { readonly label: string; count: number }>();
  for (const zuordnung of classifications) {
    if (zuordnung.isMultiValued) {
      continue;
    }
    const eintrag = zaehler.get(zuordnung.taxonomyId) ?? { label: zuordnung.taxonomyDisplayName, count: 0 };
    zaehler.set(zuordnung.taxonomyId, { label: eintrag.label, count: eintrag.count + 1 });
  }
  const hinweise: string[] = [];
  for (const eintrag of zaehler.values()) {
    const hinweis = cardinalityHint(eintrag.label, eintrag.count);
    if (hinweis !== undefined) {
      hinweise.push(hinweis);
    }
  }
  return hinweise;
}

export function validationMessageFor(fieldPath: string, _reasonCode: string): string {
  if (fieldPath === 'taxonomyTermId') {
    return 'Wähle zuerst ein Stichwort aus.';
  }
  return 'Diese Angabe wurde nicht angenommen. Lade den Kontakt neu und versuche es erneut.';
}

// --- Die Aufrufe -----------------------------------------------------------

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

function textOrNull(value: unknown): string | null | undefined {
  if (value === null) return null;
  return typeof value === 'string' ? value : undefined;
}

function classificationOf(value: unknown): ClassificationView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  const definition = textOrNull(row.definition);
  if (
    typeof row.classificationAssignmentId !== 'string' ||
    typeof row.taxonomyTermId !== 'string' ||
    typeof row.displayName !== 'string' ||
    definition === undefined ||
    typeof row.taxonomyId !== 'string' ||
    typeof row.taxonomyDisplayName !== 'string' ||
    typeof row.isMultiValued !== 'boolean' ||
    typeof row.termStatus !== 'string' ||
    typeof row.sourceType !== 'string' ||
    typeof row.assignedAt !== 'string' ||
    typeof row.validFrom !== 'string'
  ) {
    return undefined;
  }
  return {
    classificationAssignmentId: row.classificationAssignmentId,
    taxonomyTermId: row.taxonomyTermId,
    displayName: row.displayName,
    definition,
    taxonomyId: row.taxonomyId,
    taxonomyDisplayName: row.taxonomyDisplayName,
    isMultiValued: row.isMultiValued,
    termStatus: row.termStatus,
    sourceType: row.sourceType,
    assignedAt: row.assignedAt,
    validFrom: row.validFrom,
    replaced: row.replaced === true,
    replacedByDisplayName: typeof row.replacedByDisplayName === 'string' ? row.replacedByDisplayName : null,
  };
}

function endedClassificationOf(value: unknown): EndedClassificationView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  const definition = textOrNull(row.definition);
  if (
    typeof row.classificationAssignmentId !== 'string' ||
    typeof row.taxonomyTermId !== 'string' ||
    typeof row.displayName !== 'string' ||
    definition === undefined ||
    typeof row.taxonomyDisplayName !== 'string' ||
    typeof row.sourceType !== 'string' ||
    typeof row.assignedAt !== 'string' ||
    typeof row.validFrom !== 'string' ||
    typeof row.endedAt !== 'string'
  ) {
    return undefined;
  }
  return {
    classificationAssignmentId: row.classificationAssignmentId,
    taxonomyTermId: row.taxonomyTermId,
    displayName: row.displayName,
    definition,
    taxonomyDisplayName: row.taxonomyDisplayName,
    sourceType: row.sourceType,
    assignedAt: row.assignedAt,
    validFrom: row.validFrom,
    endedAt: row.endedAt,
  };
}

function termOf(value: unknown): AssignableTermView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.taxonomyTermId !== 'string' ||
    typeof row.displayName !== 'string' ||
    typeof row.definition !== 'string' ||
    typeof row.stableCode !== 'string' ||
    typeof row.taxonomyId !== 'string' ||
    typeof row.taxonomyDisplayName !== 'string' ||
    typeof row.isMultiValued !== 'boolean'
  ) {
    return undefined;
  }
  return {
    taxonomyTermId: row.taxonomyTermId,
    displayName: row.displayName,
    definition: row.definition,
    stableCode: row.stableCode,
    taxonomyId: row.taxonomyId,
    taxonomyDisplayName: row.taxonomyDisplayName,
    isMultiValued: row.isMultiValued,
  };
}

export interface ReadOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

export async function fetchClassifications(options: ReadOptions): Promise<ClassificationsResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/classifications`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const classifications: ClassificationView[] = [];
  for (const row of Array.isArray(body.classifications) ? body.classifications : []) {
    const view = classificationOf(row);
    // Eine Zeile, die den Vertrag bricht, ist ein Fehler dieses Systems --
    // dieselbe Haltung wie im Verlauf: sie wird uebersprungen und nicht halb
    // dargestellt. Der Zaehler unten bleibt die Wahrheit ueber die Zahl.
    if (view !== undefined) classifications.push(view);
  }
  const endedClassifications: EndedClassificationView[] = [];
  for (const row of Array.isArray(body.endedClassifications) ? body.endedClassifications : []) {
    const view = endedClassificationOf(row);
    if (view !== undefined) endedClassifications.push(view);
  }
  return {
    outcome: 'loaded',
    classifications,
    endedClassifications,
    additionalClassificationCount: typeof body.additionalClassificationCount === 'number' ? body.additionalClassificationCount : 0,
    endedClassificationsTruncated: body.endedClassificationsTruncated === true,
  };
}

export async function fetchAssignableTerms(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}): Promise<TermsResult> {
  const send = options.send ?? fetch;
  // OHNE Abfrageparameter: die Auswahl ist eine Liste und keine Suche, und ein
  // Suchbegriff in der Adresse landete in URL- und Proxy-Protokollen
  // (ADR-0053 Ziffer 9).
  const response = await send(`${options.baseUrl}/taxonomy-terms`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const terms: AssignableTermView[] = [];
  for (const row of Array.isArray(body.terms) ? body.terms : []) {
    const view = termOf(row);
    if (view !== undefined) terms.push(view);
  }
  return { outcome: 'loaded', terms, truncated: body.truncated === true };
}

export async function assignClassification(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly taxonomyTermId: string;
  readonly send?: SendLike;
}): Promise<AssignResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/classifications`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ taxonomyTermId: options.taxonomyTermId }),
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 201) {
    return { outcome: 'assigned' };
  }
  const reasonCode = reasonCodeOf(payload);
  if (response.status === 404) {
    // Der Server sagt mit EINEM Grundcode je Gegenstand, was fehlt -- die
    // Oberflaeche uebersetzt das in den passenden Rueckweg. Beide fuehren zum
    // Neuladen; der Unterschied ist, WAS neu geladen werden muss.
    return { outcome: 'not-found', what: reasonCode === 'taxonomy_term_not_found' ? 'term' : 'party' };
  }
  if (response.status === 422) {
    const body = (payload ?? {}) as Record<string, unknown>;
    if (reasonCode === 'taxonomy_term_not_active') {
      return { outcome: 'rejected', message: TERM_NOT_ASSIGNABLE_MESSAGE, kind: 'conflict', reasonCode };
    }
    const errors = Array.isArray(body.errors) ? (body.errors as { fieldPath: string; reasonCode: string }[]) : [];
    return { outcome: 'invalid', errors };
  }
  if (response.status === 409) {
    const message = reasonCode === 'party_not_writable' ? PARTY_NOT_WRITABLE_MESSAGE : DUPLICATE_MESSAGE;
    return { outcome: 'rejected', message, kind: 'conflict', reasonCode };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)), kind: 'other', reasonCode };
}

export async function endClassification(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly classificationAssignmentId: string;
  readonly send?: SendLike;
}): Promise<EndResult> {
  const send = options.send ?? fetch;
  const path = `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/classifications/${encodeURIComponent(options.classificationAssignmentId)}/end`;
  const response = await send(path, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    // Ein LEERER Rumpf: der Befehl kennt kein Eingabefeld, und jedes Feld
    // darin wiese der Server ab (ADR-0053 Ziffer 1).
    body: JSON.stringify({}),
  });
  const payload = await response.json().catch(() => undefined);
  if (response.ok) {
    return { outcome: 'ended' };
  }
  const reasonCode = reasonCodeOf(payload);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (response.status === 409) {
    const message = reasonCode === 'party_not_writable' ? PARTY_NOT_WRITABLE_MESSAGE : ALREADY_ENDED_MESSAGE;
    return { outcome: 'rejected', message, kind: 'conflict', reasonCode };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)), kind: 'other', reasonCode };
}
